<template>
    <section class="section-grid">
        <LeadsGridHeadline :pageKind="pageKind" />
        <mq-layout class="grid-summary-m" :mq="['sm', 'md']">
            <div class="grid-summary-inner">
                <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ leads ? leads.Total : 0 }}</span>
            </div>
            <b-input :value="usedSearchValue" :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"
                     v-show="showSearchInput"></b-input>
            <b-button class="is-transparent grid-search-m" @click="showSearchInput = true">
                <icon-base width="17" height="17" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
            </b-button>
        </mq-layout>
        <LeadsGridContent :pageKind="pageKind" @toggle-lead-edit="onToggleLeadEdit" @toggle-client-edit="onToggleClientEdit"
                          @toggle-lead-history="onToggleLeadHistory" />
        <mq-layout class="grid-summary" mq="lg">
            <div class="grid-summary-inner">
                <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ leads ? leads.Total : 0 }}</span>
                <b-tooltip position="is-bottom" type="is-dark" v-show="pageKind">
                    <span class="chart-help">
                        <icon-base width="14" height="14" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle /></icon-base>
                    </span>
                    <template #content>
                        <p>{{ $t("LeadsGraphs.LeadsBySaleStatus.TotalLeadsTooltip") }}</p>
                    </template>
                </b-tooltip>
            </div>
        </mq-layout>
        <Popup v-if="phonePopupData && phonePopupData.showPopup"
                    :visible="phonePopupData.showPopup"
                    :message="phonePopupData.message"
                    :confirmText="'OK'"
                    :cancelText="'Close'"
                    @close="closePopup" />
    </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import {Action, Getter} from "vuex-class";
import {LeadsPageViewModel, ToggleLeadEditMode} from "@/models/leads/Lead";
import {DataSourceResult} from "@/models/DataSourceResult";
import {GridColumn} from "@/models/GridColumn";
import {CompanyPermission, LeadPermissionType} from "@/models/leads/CompanyPermission";
import {LeadsPageKind} from "@/models/LeadsPageKind";
import {TableSettings, TableSettingsStateColumn} from "@/models/TableSettings";
import LeadsGridHeadline from "./LeadsGridHeadline.vue";
import LeadsGridContent from "./LeadsGridContent.vue";
import IconBase from "../IconBase.vue";
import IconSearch from "../icons/IconSearch.vue";
import i18n from "../../i18n";
import IconQuestionCircle from "@/components/icons/IconQuestionCircle.vue";
import Popup from "@/components/Leads/Popup.vue";
import { mapState } from "vuex";

const namespace = "leadsModule";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

@Component({
  components: {
    IconQuestionCircle,
    LeadsGridHeadline,
    LeadsGridContent,
    IconBase,
    IconSearch,
    Popup,
    },
    computed: {
        ...mapState("leadsModule", ["phonePopupData"]),
    },
})
export default class LeadsGrid extends Vue {
  @Prop({ required: true }) readonly pageKind!: LeadsPageKind;
  @Prop({ required: true }) readonly kindPrefix!: string;


  //#region Vuex getters and actions

  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("clientsSearchValue", { namespace }) clientsSearchValue!: string;
  @Getter("leads", { namespace }) leads!: DataSourceResult;
  @Getter("leadsPageData", { namespace }) leadsPageData!: LeadsPageViewModel;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedLeadGridColumnsIds", { namespace }) selectedLeadGridColumnsIds!: number[];
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings;
  @Action("setColumns", { namespace }) setColumns!: (columns: GridColumn[]) => void;
  @Action("setClientsSearchValue", { namespace }) setClientsSearchValue!: (searchValue: string) => void;
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  leadsGridColumns: GridColumn[] = [
    { id: 0, name: "Id", label: '', visible: true, forbidden: false, searchable: true, sortable: false, width: 80 },
    { id: 1, name: "CreateDate", label: '', class: "cell-date", searchable: false, sortable: true, visible: true, forbidden: false, width: 137 },
    { id: 2, name: "FullName", label: '', class: "cell-full-name", searchable: true, sortable: false, visible: true, forbidden: false, width: 200 },
    { id: 3, name: "Phone", label: '', class: "cell-phone", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 4, name: "Email", label: '', class: "cell-email", searchable: true, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 5, name: "Company", label: '', class: "cell-company", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 6, name: "CampaignTitle", label: '', class: "cell-channel", searchable: true, sortable: true, visible: true, forbidden: false, width: 200 },
    {
      id: 7,
      name: "MetadataTypeFromChannel",
      label: '',
      class: "cell-advertising-channel",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 25,
      name: "Source",
      label: '',
      class: "cell-source",
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 8,
      name: "ApiType",
      label: '',
      class: "cell-integration-type",
      searchable: false,
      sortable: false,
      visible: false,
      forbidden: false,
      width: 128,
    },
    {
      id: 9,
      name: "Tag",
      label: '',
      class: "cell-product-tag",
      searchable: true,
      sortable: true,
      visible: false,
      forbidden: false,
      width: 128,
    },
    {
      id: 10,
      name: "ClientStageOnCreate",
      label: '',
      class: "cell-welcome-status",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 11,
      name: "SaleStatus",
      label: '',
      class: "cell-sales-stage",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 12,
      name: "ClientStatus",
      label: '',
      class: "cell-current-sales-status",
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 13,
      name: "CallStatus",
      label: '',
      class: "cell-call-status",
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 14,
      name: "CallSecondsStr",
      label: '',
      class: "cell-call-duration",
      searchable: false,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 15,
      name: "Price",
      label: '',
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 16,
      name: "Keyword",
      label: '',
      class: "cell-keyword",
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 17,
      name: "AdGroupNameExternal",
      label: '',
      class: "cell-ad-group",
      searchable: false,
      sortable: false,
      visible: false,
      forbidden: false,
      width: 128,
    },
    {
      id: 26,
      name: "AdNameExternal",
      label: '',
      class: "cell-ad-name",
      searchable: false,
      sortable: false,
      visible: false,
      forbidden: false,
      width: 128,
    },
    {
      id: 18,
      name: "ProjectTitle",
      label: '',
      searchable: true,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 28,
      name: "Process",
      label: '',
      class: "cell-process",
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 19,
      name: "AssigneeName",
      label: '',
      class: "cell-salesperson",
      searchable: true,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
      },
    {
        id: 20,
        name: "MeetingStartDate",
        label: '',
        class: "cell-meeting-date",
        searchable: false,
        sortable: false,
        visible: true,
        forbidden: false,
        width: 128,
    },
    {
        id: 21,
        name: "MeetingStatus",
        label: '',
        class: "cell-meeting-status",
        searchable: true,
        sortable: false,
        visible: true,
        forbidden: false,
        width: 128,
    },
    {
      id: 22,
      name: "DealAmount",
      label: '',
      class: "cell-transaction-sum",
      sortable: true,
      searchable: true,
      visible: false,
      forbidden: false,
      width: 128,
    },
    {
      id: 23,
      name: "AssetTitle",
      label: '',
      searchable: true,
      sortable: true,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 24,
      name: "Address",
      label: '',
      searchable: true,
      sortable: true,
      visible: false,
      forbidden: false,
      width: 128,
      },
    {
      id: 27,
      name: "CampaignIDView",
      label: '',
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
      width: 128,
    },
    {
      id: 1000,
      name: "Actions",
      label: '',
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
    },
  ];

  isPopupVisible = false;
  popupMessage = "";
  showSearchInput = false;

  //#endregion Properties


  //#region Watchers

  @Watch("tableSettings", { immediate: true })
  onTableSettingsChange(newValue: TableSettings, oldValue: TableSettings): void {
    if (newValue === undefined) {
      return;
    } else {
      this.updateColumns(newValue);
    }
  }

  @Watch("leadsPageData", { immediate: true })
  onLeadsPageDataChange(newValue: LeadsPageViewModel, oldValue: LeadsPageViewModel): void {
    if (oldValue === undefined || newValue === null) {
      return;
    } else {
      this.updateColumns(this.tableSettings);
    }
  }

  @Watch("pageKind", { immediate: true })
  onPageKindChange(newValue: LeadsPageKind, oldValue: LeadsPageKind): void {
    this.leadsGridColumns.forEach((clmn: GridColumn) => clmn.label = i18n.tc(`${this.kindPrefix}.Grid.Columns.${clmn.name}`));
    this.setColumns(this.leadsGridColumnsFiltered);
  }

  //#endregion Watchers


  //#region Getters  

    get leadsGridColumnsFiltered(): GridColumn[] {
        const excludeColumnIds = this.getExcludeColumnsIdByPageKind(this.pageKind);

        let leadsGridColumns = this.leadsGridColumns.filter(column => excludeColumnIds.indexOf(column.id) === -1)

        // if no selected columns (like on first page load), select all columns
        if (this.selectedLeadGridColumnsIds.length === 0) {
            for (let i = 0; i < leadsGridColumns.length; i++) {
                if (leadsGridColumns[i].visible && !leadsGridColumns[i].forbidden) {
                    this.selectedLeadGridColumnsIds.push(leadsGridColumns[i].id);
                }
            }
        }

        for (const column of leadsGridColumns) {
            column.visible = this.selectedLeadGridColumnsIds.indexOf(column.id) !== -1;
        }

        return leadsGridColumns;
    }

  get showAssignee(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowSalesman;
  }

  get showCampaignNameInLeads(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.CampaignNameInLeads);
  }

  get showChannelMetadataType(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Source);
  }

  get showCommitments(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Project);
  }

  get showDealsAmount(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowDealsAmount;
  }

  get showEmail(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowEmail;
  }

  get showFullname(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowFullname;
  }

  get showPhone(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowPhone;
  }

  get showPrice(): boolean {
    return this.leadsPageData && this.leadsPageData.ShowPrice && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Price);
  }

  get showKeywords(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.LeadsScreenKeywords);
  }

  get showTags(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Tags);
  }

  //#region Getters based on page kind

  get usedSearchValue(): string {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSearchValue;
      case LeadsPageKind.Leads:
        return this.searchValue;
    }
  }

  //#endregion Getters based on page kind

  //#endregion Getters


  //#region Lifecycle

    mounted(): void {
        this.$store.dispatch("leadsModule/checkPhonePopup")
            .then((response) => {
                if (response.showPopup) {
                    this.isPopupVisible = true;
                    this.popupMessage = response.message;
                }
            })
            .catch((error) => {
                console.error("Error in checkPhonePopup:", error);
            });
    }

  //#endregion Lifecycle


  //#region Handlers

  onSearchChange(e: Event) {
    this.setUsedSearchValue((e.target as HTMLInputElement).value);
  }

  onToggleClientEdit(mode: ToggleLeadEditMode) {
    this.$emit('toggle-client-edit', mode);
  }

  onToggleLeadEdit(mode: ToggleLeadEditMode) {
    this.$emit('toggle-lead-edit', mode);
  }

  onToggleLeadHistory() {
    this.$emit('toggle-lead-history');
  }

    closePopup(): void {
        this.isPopupVisible = false;
        this.$store.commit("leadsModule/setPhonePopupData", { showPopup: false, message: "" });
    }
  //#endregion Handlers


  //#region Private methods

    onPopupConfirm(): void {
        this.closePopup();
    }

    onPopupCancel(): void {
        this.closePopup();
    }

  private updateColumns(tableSettings: TableSettings) {
    const newColumns: GridColumn[] = JSON.parse(JSON.stringify(this.columns));
    for (let i = 0; i < newColumns.length; i++) {
      let tableSettingsColumn: TableSettingsStateColumn | undefined = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
        let columnName: string | undefined = column.field;
        columnName = this.updateColumnsAlterColumnName(columnName);
        return columnName === newColumns[i].name
      });
      if (!tableSettingsColumn) {
        tableSettingsColumn = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
          let columnName: string | undefined = column.title;
          columnName = this.updateColumnsAlterColumnName(columnName);
          return columnName === newColumns[i].name;
        });
      }

      switch (newColumns[i].name) {
        case "MetadataTypeFromChannel":
          newColumns[i].visible = this.showChannelMetadataType && (!tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden);
          break;
        case "Keyword":
          newColumns[i].visible = this.showKeywords && (!tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden);
          break;
        case "ApiType":
          newColumns[i].visible = this.showChannelMetadataType && (!tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden);
          break;
        default:
          newColumns[i].visible = !tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden;
          break;
      }
      switch (newColumns[i].name) {
          case "DealAmount":
          newColumns[i].forbidden = !this.showDealsAmount
          break;
        case "AssigneeName":
          newColumns[i].forbidden = !this.showAssignee
          break;
        case "CampaignTitle":
          newColumns[i].forbidden = !this.showCampaignNameInLeads
          break;
        case "Email":
          newColumns[i].forbidden = !this.showEmail
          break;
        case "FullName":
          newColumns[i].forbidden = !this.showFullname
          break;
        case "Keyword":
          newColumns[i].forbidden = !this.showKeywords
          break;
        case "Phone":
          newColumns[i].forbidden = !this.showPhone
          break;
        case "Price":
          newColumns[i].forbidden = !this.showPrice
          break;
        case "ProjectTitle":
          newColumns[i].forbidden = !this.showCommitments
          break;
        case "Tag":
          newColumns[i].forbidden = !this.showTags
          break;
        case "MetadataTypeFromChannel":
        case "ApiType":
          newColumns[i].forbidden = !this.showChannelMetadataType
          break;
      }

      if (tableSettingsColumn && tableSettingsColumn.hidden) {
        newColumns[i].visible = false;
      }
    }
    this.setColumns(newColumns);
  }

  private updateColumnsAlterColumnName(columnName: string | undefined): string | undefined {
    switch (columnName) {
      case "Actions":
      case "פעולות":
        return "Actions";
      case "Ad Group":
      case "קבוצת מודעות":
      case "קבוצת מודעות אחרונה":
        return "AdGroupNameExternal";
      case "CallSeconds":
        return "CallSecondsStr";
      case "Keyword":
      case "מילת מפתח":
      case "מילת מפתח אחרונה":
        return "Keyword";
    }
    return columnName;
  }

  private getExcludeColumnsIdByPageKind(pageKind: LeadsPageKind): number[] {
    if (pageKind === LeadsPageKind.Leads) {
      return [10, 11, 12, 19, 20, 21, 25, 27];
    }

    return [];
  }

  //#region Setters based on page kind

  setUsedSearchValue(searchValue: string): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSearchValue(searchValue);
        break;
      case LeadsPageKind.Leads:
        this.setSearchValue(searchValue);
        break;
    }
  }

  //#endregion Setters based on page kind

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
.section-grid {
  background: #fff;
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  margin-bottom: 30px;
}

.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;

  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}

.grid-summary-m {
  height: 49px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #435c65;

  .button.grid-search-m {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 49px;
  }

  .grid-summary-inner {
    padding: 10px 15px;

    .summary-label {
      font-weight: 600;
    }
  }
}
</style>
